import { makeStyles } from 'tss-react/mui';

import { HEADER_HEIGHT } from '../header/GlobalHeader.styles';

export const NAV_DRAWER_OPEN_WIDTH = 300;
export const NAV_DRAWER_LARGE_UP_CLOSED_WIDTH = 72;
export const NAV_DRAWER_CLOSED_WIDTH = 108;

const DRAWER_BORDER_WIDTH = 1;

const useStyles = makeStyles<
  {
    isMinimal?: boolean;
  },
  'navDrawerList'
>()((theme, { isMinimal }, classes) => {
  const drawerBorder = `${DRAWER_BORDER_WIDTH}px solid ${theme.new.color.line.hairline}`;
  const enteringScreenTransition = {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  };
  const leavingScreenTransition = {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  };

  const commonDrawerStyles = {
    zIndex: theme.zIndex.drawer,
    display: 'flex',
    flexDirection: 'column' as const,
    backgroundColor: theme.palette.common.white,
    overflow: 'hidden',
  };

  /** Matches nav row vertical rhythm (`NavDrawerItem` ~56px touch row). */
  const footerPaddingExpanded = `${theme.new.spacing.medium} ${theme.new.spacing.large}`;

  return {
    drawer: {
      flexShrink: 0,
      whiteSpace: 'nowrap',
      backgroundColor: theme.palette.common.white,
      border: 'none',
      [theme.breakpoints.down('lg')]: {
        flex: `${NAV_DRAWER_LARGE_UP_CLOSED_WIDTH}px 1 0`,
      },
      [theme.breakpoints.down('md')]: {
        flex: '1',
        ...(isMinimal && {
          flex: `0 0 ${NAV_DRAWER_LARGE_UP_CLOSED_WIDTH}px`,
          maxWidth: `${NAV_DRAWER_LARGE_UP_CLOSED_WIDTH}px`,
        }),
      },
      [theme.breakpoints.up('md')]: {
        borderRight: drawerBorder,
      },
      '& svg.integrationIcon, & .MuiListItem-button:hover svg.integrationIcon':
        {
          fill: 'none',
        },
    },

    logoContainer: {
      position: 'relative',
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      justifyContent: 'space-between',
      maxHeight: `${HEADER_HEIGHT}px`,
      minHeight: `${HEADER_HEIGHT}px`, //added to avoid the shift of the logo in smaller screens
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },

      '& .appLogo': {
        display: 'flex',
        alignItems: 'center',
        lineHeight: 0,
        padding: '20px 16px',
        marginLeft: '35px',
        [theme.breakpoints.down('md')]: {
          marginLeft: isMinimal ? '4px' : '35px',
        },
        '& svg': {
          flexShrink: 0,
          height: 32,
          maxHeight: 32,
          width: 'auto',
        },
        '&.isOpen': {
          [theme.breakpoints.down('md')]: {
            marginLeft: '4px',
          },
          [theme.breakpoints.up('md')]: {
            marginLeft: 0,
          },
        },

        [theme.breakpoints.up('md')]: {
          padding: '16px 20px',
          marginLeft: 0,
        },
      },

      '& .menu': {
        position: 'absolute',
        left: '8px',
        backgroundColor: theme.new.color.common.white,

        '& svg': {
          width: '24px',
          height: '24px',
        },

        [theme.breakpoints.up('md')]: {
          display: 'none',
        },

        '&.isOpen': {
          display: 'none',
        },

        '&:hover': {
          background: theme.new.color.common.white,
        },
      },

      '& .toggle': {
        display: 'none',
        '&.isOpen': {
          display: 'block',
          [theme.breakpoints.up('md')]: {
            display: 'none',
          },
        },
      },
    },

    drawerBody: {
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
      width: '100%',
    },

    divider: {
      margin: 0,
      borderColor: theme.new.color.line.hairline,
    },

    itemDivider: {
      margin: '8px 0',
      borderColor: theme.new.color.line.hairline,
    },

    drawerBottomSplit: {
      boxSizing: 'border-box',
      minHeight: '56px',
      width: '100%',
    },

    drawerBottomSplitMinimized: {
      gap: theme.new.spacing.xsmall,
      minHeight: 'auto',
    },

    drawerCollapseCell: {
      alignItems: 'center',
      display: 'flex',
      flexShrink: 0,
      justifyContent: 'flex-end',
      paddingBlock: theme.new.spacing.medium,
      paddingInlineEnd: theme.new.spacing.large,
      paddingInlineStart: theme.new.spacing.xsmall,
    },

    drawerCollapseOnlyWrap: {
      display: 'flex',
      justifyContent: 'flex-end',
      padding: footerPaddingExpanded,
      '& button': {
        padding: 0,
      },
    },

    drawerCollapseRailCollapsed: {
      alignItems: 'center',
      display: 'flex',
      flex: 'none',
      justifyContent: 'center',
      paddingBottom: theme.new.spacing.xsmall,
      paddingInline: theme.new.spacing.xsmall,
      paddingTop: 0,
      width: '100%',
    },

    drawerFeedbackGrow: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      minWidth: 0,
    },

    drawerFeedbackRailCollapsed: {
      alignItems: 'flex-start',
      display: 'flex',
      flex: 'none',
      justifyContent: 'center',
      minWidth: 0,
      width: '100%',
    },

    drawerFooterStandalone: {
      flexShrink: 0,
      width: '100%',
    },

    titleContainer: {
      display: 'none',
      padding: '16px 24px',
      '&.isOpen': {
        display: 'block',
      },
    },

    backToLinkContainer: {
      display: 'none',
      padding: '32px 24px 8px',
      '&.isOpen': {
        display: 'block',
      },
    },

    drawerOpen: {
      ...commonDrawerStyles,
      transition: theme.transitions.create('width', enteringScreenTransition),
      width: '75%',
      [theme.breakpoints.up('md')]: {
        width: `${NAV_DRAWER_OPEN_WIDTH}px`,
        // Labels wrap, so the list keeps its open width while the drawer
        // animates; otherwise every long label reflows on each frame.
        [`& .${classes.navDrawerList}`]: {
          minWidth: `${NAV_DRAWER_OPEN_WIDTH - DRAWER_BORDER_WIDTH}px`,
        },
      },
    },

    drawerClose: {
      ...commonDrawerStyles,
      transition: theme.transitions.create('width', leavingScreenTransition),
      maxHeight: `${HEADER_HEIGHT}px`,
      width: `${NAV_DRAWER_CLOSED_WIDTH}px`,
      borderRight: 'none',
      borderBottom: drawerBorder,

      '& .MuiListItem-root': {
        display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'flex',
        },
      },

      [theme.breakpoints.up('md')]: {
        borderRight: drawerBorder,
        borderBottom: 'none',
        maxHeight: '100vh',
        overflow: 'visible',
        width: `${NAV_DRAWER_LARGE_UP_CLOSED_WIDTH}px`,
      },

      ...(isMinimal && {
        [theme.breakpoints.down('md')]: {
          borderRight: drawerBorder,
          borderBottom: 'none',
          maxHeight: '100vh',
          overflow: 'visible',
          width: `${NAV_DRAWER_LARGE_UP_CLOSED_WIDTH}px`,
        },
      }),
    },

    navDrawerList: {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      width: '100%',
      paddingTop: theme.new.spacing.medium,
      overflowY: 'auto',
      overflowX: 'hidden',
    },

    drawerToggle: {
      display: 'block',
      marginBlockStart: 'auto',
      flexShrink: 0,
      width: '100%',

      '&:not(.isOpen)': {
        display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          '& .MuiIconButton-root svg': {
            transform: 'rotate(180deg)',
            transformOrigin: 'center',
          },
        },
      },

      '&.isOpen': {
        display: 'none',
        [theme.breakpoints.up('md')]: {
          display: 'block',
        },
      },

      button: {
        '&:focus-visible': {
          borderRadius: 0,
          outlineOffset: '1px',
          outline: `2px auto ${theme.new.color.brandPrimary}`,
        },
      },
    },

    list: {
      overflowY: 'auto',
      overflowX: 'hidden',
      top: '0',
      paddingTop: '0',

      '&.viewAsHeader li:first-of-type': {
        marginTop: '50px',
      },

      '& a': {
        whiteSpace: 'break-spaces',
      },

      [theme.breakpoints.down('lg')]: {
        '& .MuiListItem-root': {
          height: '60px',
        },
      },
    },

    closeButton: {
      justifyContent: 'flex-end',
      padding: '0',
      [theme.breakpoints.up('lg')]: {
        display: 'none',
      },
    },

    closeIcon: {
      fontSize: '24px',
    },

    assetsIcon: {
      width: '25px',
      fill: theme.palette.text.primary,
    },

    dashboardIcon: {
      margin: '0 0 0 2px',
    },

    alertsIcon: {
      margin: '0 0 0 2px',
    },

    dispatchIcon: {
      width: '25px',
      fill: theme.palette.text.primary,
    },
  };
});

export default useStyles;
